import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Calculator, Wand2, Timer, Check, Loader2, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const CALCUL_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/wqswva2n_gemini_generated_video_37fc32c6.mp4";

function Field({ label, children }) {
  return (
    <div>
      <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-1.5">{label}</p>
      <p className="text-foreground/80 leading-relaxed">{children}</p>
    </div>
  );
}

function TipPanel({ lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.calcul.tip.${k}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/calcul/tip-of-day", { params: { refresh, lang }, timeout: 60000 });
      setData(res);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [lang]); // eslint-disable-line

  return (
    <div className="text-center" data-testid="calcul-tip">
      {loading && !data ? (
        <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr("loading")}
        </p>
      ) : data ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={data.title}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-xl mx-auto space-y-5 text-left"
          >
            <p className="font-display text-gradient-orange text-2xl sm:text-3xl text-center">{data.title}</p>
            {data.trick && <p className="text-foreground/80 leading-[1.8] text-center">{data.trick}</p>}
            {data.example && (
              <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] px-5 py-4">
                <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-1.5">{tr("exampleLabel")}</p>
                <p className="font-display text-lg text-foreground/90 whitespace-pre-line">{data.example}</p>
              </div>
            )}
            {data.why && <Field label={tr("whyLabel")}>{data.why}</Field>}
          </motion.div>
        </AnimatePresence>
      ) : (
        <p className="font-display italic text-foreground/40 py-7">{tr("error")}</p>
      )}

      <div className="mt-7">
        <button
          onClick={() => load(true)}
          disabled={loading}
          data-testid="calcul-tip-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {tr("refresh")}
        </button>
      </div>
    </div>
  );
}

function DecodePanel({ lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.calcul.decode.${k}`);
  const presets = t("landing.calcul.decode.presets", { returnObjects: true }) || [];
  const [expr, setExpr] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async (value) => {
    const q = (value ?? expr).trim();
    if (!q) return;
    setExpr(q);
    setLoading(true);
    try {
      const { data: res } = await api.get("/calcul/decode", { params: { expr: q, lang }, timeout: 60000 });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("error"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="text-foreground/60 leading-[1.8] text-center max-w-xl mx-auto mb-6">{tr("subtitle")}</p>
      <form
        onSubmit={(e) => { e.preventDefault(); run(); }}
        className="flex flex-col sm:flex-row gap-2.5 max-w-xl mx-auto mb-4"
      >
        <input
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          placeholder={tr("placeholder")}
          data-testid="calcul-decode-input"
          className="flex-1 rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors"
        />
        <button
          type="submit"
          disabled={loading || !expr.trim()}
          data-testid="calcul-decode-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {tr("go")}
        </button>
      </form>

      <div className="flex flex-wrap justify-center gap-2 mb-2">
        {presets.map((p, i) => (
          <button
            key={i}
            onClick={() => run(p)}
            disabled={loading}
            data-testid={`calcul-decode-preset-${i}`}
            className="text-xs text-foreground/55 border border-white/10 rounded-full px-3 py-1.5 hover:text-primary hover:border-primary/30 transition-colors disabled:opacity-50"
          >
            {p}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr("loading")}
        </p>
      )}

      <AnimatePresence mode="wait">
        {!loading && data && (
          <motion.div
            key={data.expr}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="mt-6 space-y-5 text-left max-w-xl mx-auto"
            data-testid="calcul-decode-result"
          >
            <div className="text-center">
              <p className="font-display text-gradient-orange text-2xl sm:text-3xl">{data.expr} = {data.result}</p>
              {data.method && <p className="text-xs uppercase tracking-[0.2em] text-primary/70 mt-1">{tr("methodLabel")} · {data.method}</p>}
            </div>
            {Array.isArray(data.steps) && data.steps.length > 0 && (
              <div>
                <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-2">{tr("stepsLabel")}</p>
                <ol className="space-y-2">
                  {data.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-foreground/80 leading-relaxed">
                      <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full border border-primary/40 text-primary text-xs font-display">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {data.wonder && <Field label={tr("wonderLabel")}>{data.wonder}</Field>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const DOJO_DURATION = 60;

function ri(a, b) {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}

function genQuestion(level) {
  let text, answer;
  if (level === "facile") {
    const t = ri(0, 2);
    if (t === 0) { const a = ri(11, 49), b = ri(11, 49); text = `${a} + ${b}`; answer = a + b; }
    else if (t === 1) { const a = ri(20, 90), b = ri(1, a); text = `${a} − ${b}`; answer = a - b; }
    else { const a = ri(2, 9), b = ri(2, 9); text = `${a} × ${b}`; answer = a * b; }
  } else if (level === "moyen") {
    const t = ri(0, 3);
    if (t === 0) { const a = ri(12, 29), b = ri(3, 9); text = `${a} × ${b}`; answer = a * b; }
    else if (t === 1) { const a = ri(100, 499), b = ri(50, 499); text = `${a} + ${b}`; answer = a + b; }
    else if (t === 2) { const a = ri(10, 99); text = `${a} × 11`; answer = a * 11; }
    else { const a = ri(2, 9); text = `${a}5²`; answer = (a * 10 + 5) ** 2; }
  } else {
    const t = ri(0, 3);
    if (t === 0) { const a = ri(12, 99), b = ri(11, 29); text = `${a} × ${b}`; answer = a * b; }
    else if (t === 1) { const a = ri(2, 9); text = `${a}5²`; answer = (a * 10 + 5) ** 2; }
    else if (t === 2) { const base = ri(2, 9) * 20, pct = [10, 25, 50][ri(0, 2)]; text = `${pct} % de ${base}`; answer = (base * pct) / 100; }
    else { const a = ri(200, 899), b = ri(100, a); text = `${a} − ${b}`; answer = a - b; }
  }
  return { text, answer };
}

function DojoPanel({ lang }) {
  const { t } = useTranslation();
  const tr = (k, opts) => t(`landing.calcul.dojo.${k}`, opts);
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [level, setLevel] = useState("moyen");
  const [q, setQ] = useState(null);
  const [value, setValue] = useState("");
  const [correct, setCorrect] = useState(0);
  const [attempted, setAttempted] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DOJO_DURATION);
  const [flash, setFlash] = useState(null); // "ok" | "no"

  useEffect(() => {
    if (phase !== "running") return;
    if (timeLeft <= 0) { setPhase("done"); return; }
    const id = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, timeLeft]);

  const start = () => {
    setCorrect(0); setAttempted(0); setValue(""); setFlash(null);
    setTimeLeft(DOJO_DURATION); setQ(genQuestion(level)); setPhase("running");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!value.trim() || !q) return;
    const ok = Number(value.trim().replace(",", ".")) === q.answer;
    setAttempted((n) => n + 1);
    if (ok) setCorrect((n) => n + 1);
    setFlash(ok ? "ok" : "no");
    setTimeout(() => setFlash(null), 260);
    setQ(genQuestion(level));
    setValue("");
  };

  const tier = correct >= 15 ? "tierHigh" : correct >= 7 ? "tierMid" : "tierLow";
  const LEVELS = ["facile", "moyen", "difficile"];

  if (phase === "idle") {
    return (
      <div className="text-center max-w-xl mx-auto" data-testid="dojo-idle">
        <p className="text-foreground/60 leading-[1.8] mb-6">{tr("intro")}</p>
        <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-3">{tr("levelLabel")}</p>
        <div className="flex justify-center gap-2 mb-8">
          {LEVELS.map((lv) => (
            <button
              key={lv}
              onClick={() => setLevel(lv)}
              data-testid={`dojo-level-${lv}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                level === lv ? "bg-primary/15 text-primary border-primary/40" : "text-foreground/60 border-white/10 hover:text-primary hover:border-primary/30"
              }`}
            >
              {tr(`levels.${lv}`)}
            </button>
          ))}
        </div>
        <button
          onClick={start}
          data-testid="dojo-start"
          className="btn-frost-orange inline-flex items-center gap-3 px-8 py-3.5 rounded-full font-semibold text-base"
        >
          <Timer size={18} /> {tr("start")}
        </button>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="text-center max-w-xl mx-auto py-4" data-testid="dojo-result">
        <p className="font-display text-gradient-orange text-3xl sm:text-4xl mb-3">{tr("resultTitle")}</p>
        <p className="font-display text-6xl text-primary mb-2" data-testid="dojo-final-score">{correct}</p>
        <p className="text-foreground/70 mb-5">{tr("scoreLine", { n: correct, total: attempted })}</p>
        <p className="font-display italic text-lg text-foreground/85 mb-8 leading-relaxed">{tr(tier)}</p>
        <button
          onClick={() => setPhase("idle")}
          data-testid="dojo-replay"
          className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
        >
          <RefreshCw size={16} /> {tr("replay")}
        </button>
      </div>
    );
  }

  const pct = (timeLeft / DOJO_DURATION) * 100;
  return (
    <div className="max-w-xl mx-auto" data-testid="dojo-running">
      <div className="flex items-center justify-between mb-5 text-sm">
        <span className="text-foreground/60">{tr("scoreLabel")} · <span className="text-primary font-display text-lg" data-testid="dojo-score">{correct}</span></span>
        <span className="text-foreground/60 flex items-center gap-1.5"><Timer size={14} className="text-primary" /> <span data-testid="dojo-timer" className="tabular-nums">{timeLeft}s</span></span>
      </div>
      <div className="h-1 rounded-full bg-white/10 overflow-hidden mb-8">
        <div className="h-full bg-primary transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
      </div>

      <div className={`text-center transition-colors duration-200 ${flash === "ok" ? "text-emerald-300" : flash === "no" ? "text-red-300" : "text-foreground"}`}>
        <p className="font-display text-5xl sm:text-6xl mb-8 tracking-tight" data-testid="dojo-question">{q?.text}</p>
      </div>

      <form onSubmit={submit} className="flex gap-2.5 max-w-xs mx-auto">
        <input
          autoFocus
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={tr("answerPlaceholder")}
          data-testid="dojo-input"
          className="flex-1 text-center text-lg rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors"
        />
        <button
          type="submit"
          data-testid="dojo-submit"
          className="inline-flex items-center justify-center rounded-full bg-primary/15 border border-primary/40 text-primary w-12 h-12 hover:bg-primary/25 transition-colors"
        >
          <Check size={18} />
        </button>
      </form>
    </div>
  );
}

export function CalculMental() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [active, setActive] = useState(0);
  const TABS = [
    { key: "tip", icon: Calculator, label: "tabTip" },
    { key: "decode", icon: Wand2, label: "tabDecode" },
    { key: "dojo", icon: Timer, label: "tabDojo" },
  ];

  return (
    <div data-testid="calcul-mental">
      <div className="max-w-2xl mx-auto mb-8">
        <CinematicVideo
          src={CALCUL_VIDEO}
          testId="calcul-cinematic-video"
          caption={t("landing.calcul.subtitle")}
          fade
          noSound
        />
      </div>
      <div className="text-center mb-7">
        <h3 className="font-display text-gradient-orange text-3xl sm:text-4xl leading-tight mb-3">{t("landing.calcul.title")}</h3>
        <p className="text-foreground/55 italic max-w-md mx-auto">{t("landing.calcul.subtitle")}</p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-7" role="tablist">
        {TABS.map((tab, i) => {
          const Icon = tab.icon;
          const on = active === i;
          return (
            <button
              key={tab.key}
              onClick={() => setActive(i)}
              data-testid={`calcul-tab-${tab.key}`}
              className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors border ${
                on ? "bg-primary/15 text-primary border-primary/40" : "text-foreground/60 border-white/10 hover:text-primary hover:border-primary/30"
              }`}
            >
              <Icon size={15} strokeWidth={1.8} className="flex-shrink-0" /> {t(`landing.calcul.${tab.label}`)}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
        >
          {active === 0 ? <TipPanel lang={lang} /> : active === 1 ? <DecodePanel lang={lang} /> : <DojoPanel lang={lang} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
